import { useMemo } from 'react'
import { useQuery } from 'urql'
import { INBOX_LOCATIONS_QUERY } from 'app/graphql/inboxLocations'
import type { LocationJson } from 'app/types/entities'
import type { InboxLocationsResult } from 'app/graphql/inboxLocations'

/**
 * Inbox entitlement is per *location*, not per workspace.
 *
 * Products live only on `Location.products` (see
 * `docs/specs/2026-06-22-sms-agent-product-toggle-design.md`); the workspace-level
 * `products` column was removed and backfilled down onto every location.
 */
export function hasInboxProduct(location: { products?: string[] | null }): boolean {
  return location.products?.includes(`Inbox`) ?? false
}

/**
 * The locations that actually have an inbox: those with the `Inbox` product
 * enabled, sorted by name.
 *
 * The filter is strict — a location with no products at all is not entitled to an
 * inbox, so an absent array means "no products", not "not configured yet". That is
 * safe because the `me.products` array gating the Inbox nav item is the *union* of
 * these same per-location arrays: the nav shows Inbox only when at least one
 * location carries it, so a reachable inbox can never filter down to nothing.
 * Workspaces where no location carries the flag are precisely the workspaces that
 * are not entitled to the Inbox product, and their nav item is already hidden.
 */
export function inboxEnabledLocations(locations: LocationJson[]): LocationJson[] {
  return locations.filter(hasInboxProduct).sort((a, b) => (a.name ?? ``).localeCompare(b.name ?? ``))
}

/**
 * Inbox-enabled locations for the current workspace.
 *
 * Two callers share this: the picker lists them, and the subnav toolbar uses the
 * count to decide whether a picker is worth showing at all. Both subscribe to the
 * same urql document, so the pair costs one request.
 */
export function useInboxLocations(): { locations: LocationJson[]; loading: boolean } {
  const [{ data, fetching }] = useQuery<InboxLocationsResult>({
    query: INBOX_LOCATIONS_QUERY,
    variables: { pagination: { limit: 200 } },
  })

  const locations = useMemo(() => inboxEnabledLocations(data?.locations?.hits ?? []), [data])

  return { locations, loading: fetching }
}
